import { Injectable, effect, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { BehaviorSubject, EMPTY, Observable, OperatorFunction, combineLatest } from 'rxjs';
import { catchError, filter, map, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import { ITeam, ITeamPOST, ITeamPUT, IUser, UserApiService, deriveMyTeams } from 'shared';

import { AppStateService } from './state';

@Injectable({ providedIn: 'root' })
export class TeamService {
    private readonly _teams = new BehaviorSubject<Array<ITeam>>([]);
    private readonly _teamsLoaded = new BehaviorSubject<boolean>(false);

    public readonly teams$ = this._teams.asObservable();

    private readonly userApi = inject(UserApiService);
    private readonly appState = inject(AppStateService);

    // Emits only once the teams have actually been fetched, so an empty result means "no teams" rather than "not loaded yet".
    public readonly myTeams$: Observable<Array<ITeam>> = combineLatest([
        this._teamsLoaded,
        this.teams$,
        this.appState.user.distinctStream,
    ]).pipe(
        filter(([loaded]) => loaded),
        map(([, teams, user]) => deriveMyTeams(teams, user?.id)),
        shareReplay({ bufferSize: 1, refCount: true })
    );

    public readonly myTeamIds$: Observable<ReadonlySet<string>> = this.myTeams$.pipe(
        map((teams) => new Set(teams.map((team) => team.id))),
        shareReplay({ bufferSize: 1, refCount: true })
    );

    public readonly defaultTeam$: Observable<ITeam | null> = combineLatest([this._teamsLoaded, this.teams$]).pipe(
        filter(([loaded]) => loaded),
        map(([, teams]) => teams.find((team) => team.default) ?? null),
        shareReplay({ bufferSize: 1, refCount: true })
    );

    public constructor() {
        this.refreshTeamsOnLoggedInChange();
    }

    public refreshTeams(): Observable<Array<ITeam>> {
        return this.userApi.getTeams().pipe(
            tap((teams) => {
                this._teams.next(teams);
                this._teamsLoaded.next(true);
            }),
            catchError((err) => {
                console.error('Failed to refresh teams', err);
                this._teams.next([]);
                this._teamsLoaded.next(true);
                return EMPTY;
            })
        );
    }

    public getTeam(teamId: string): Observable<ITeam | undefined> {
        return this.teams$.pipe(map((teams) => teams.find((t) => t.id === teamId)));
    }

    public getTeamWhenLoaded(teamId: string): Observable<ITeam | null> {
        return this._teamsLoaded.pipe(
            filter(Boolean),
            take(1),
            map(() => this._teams.value.find((t) => t.id === teamId) ?? null)
        );
    }

    public getUsersForTeam(teamId: string): Observable<Array<IUser>> {
        return this.getTeam(teamId).pipe(map((team) => team?.users ?? []));
    }

    public createTeam(team: ITeamPOST): Observable<ITeam> {
        return this.userApi.createTeam(team).pipe(this.reloadCache());
    }

    public updateTeam(teamId: string, team: ITeamPUT): Observable<ITeam> {
        return this.userApi.updateTeam(teamId, team).pipe(this.reloadCache());
    }

    public archiveTeam(teamId: string): Observable<boolean> {
        return this.userApi.archiveTeam(teamId).pipe(this.reloadCache());
    }

    public addUsersToTeam(teamId: string, userIds: Array<string>): Observable<void> {
        return this.userApi.addUsersToTeam(teamId, { userIds }).pipe(this.reloadCache());
    }

    public removeUserFromTeam(teamId: string, userId: string): Observable<void> {
        return this.userApi.removeUserFromTeam(teamId, userId).pipe(this.reloadCache());
    }

    private refreshTeamsOnLoggedInChange(): void {
        const loggedInWithTenant = toSignal(this.appState.loggedInWithTenant.distinctStream);
        effect(() => {
            const loggedIn = loggedInWithTenant();
            if (loggedIn) {
                this.refreshTeams().pipe(take(1)).subscribe();
            } else {
                this._teams.next([]);
                this._teamsLoaded.next(false);
            }
        });
    }

    private reloadCache<TValue>(): OperatorFunction<TValue, TValue> {
        return (source: Observable<TValue>) =>
            source.pipe(switchMap((value) => this.refreshTeams().pipe(map(() => value))));
    }
}
